import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/api';

interface JobStatus {
  jobId: string;
  state: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';
  progress: number;
  failedReason?: string;
  createdAt: string;
  finishedAt?: string;
  queuePosition?: number;
  totalInQueue?: number;
  estimatedStartTime?: string;
  tokensUsed?: number;
  message?: string;
}

interface TaxonomyJobProgressProps {
  jobId: string;
  onComplete: () => void;
  onCancel?: () => void;
}

const POLL_INTERVAL = 5000; // 5 seconds

export default function TaxonomyJobProgress({ jobId, onComplete, onCancel }: TaxonomyJobProgressProps) {
  const { t } = useTranslation();
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showPrioritizeModal, setShowPrioritizeModal] = useState(false);
  const [isPrioritizing, setIsPrioritizing] = useState(false);

  // Poll job status
  useEffect(() => {
    if (!jobId) return;

    const pollStatus = async () => {
      try {
        const response = await apiClient.get(`/taxonomy/generate-ai/status/${jobId}`);
        const status: JobStatus = response.data.data;
        setJobStatus(status);

        // Stop polling if completed or failed
        if (status.state === 'completed') {
          setTimeout(() => {
            onComplete();
          }, 2000);
        } else if (status.state === 'failed') {
          setError(status.failedReason || 'Taxonomy generation failed');
        }
      } catch (err: any) {
        console.error('Failed to poll job status:', err);
        // If job not found (404), assume it was canceled or removed
        if (err.response?.status === 404) {
          if (onCancel) {
            onCancel();
          }
          return; // Stop polling
        }
        setError(err.response?.data?.message || 'Failed to check job status');
      }
    };

    const interval = setInterval(pollStatus, POLL_INTERVAL);
    pollStatus(); // Poll immediately

    return () => clearInterval(interval);
  }, [jobId, onComplete, onCancel]);

  const getElapsedTime = (): string => {
    if (!jobStatus?.createdAt) return '0s';
    const elapsed = Math.floor((Date.now() - new Date(jobStatus.createdAt).getTime()) / 1000);
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  };

  const getTimeUntilStart = (): string => {
    if (!jobStatus?.estimatedStartTime) return '';
    const timeUntil = Math.floor((new Date(jobStatus.estimatedStartTime).getTime() - Date.now()) / 1000);
    if (timeUntil <= 0) return t('settings.taxonomy.startingSoon', 'Starting soon...');
    const minutes = Math.floor(timeUntil / 60);
    const seconds = timeUntil % 60;
    return minutes > 0 ? `~${minutes}m ${seconds}s` : `~${seconds}s`;
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    try {
      setIsCanceling(true);
      await apiClient.delete(`/taxonomy/generate-ai/${jobId}`);
      setShowCancelModal(false);
      if (onCancel) {
        onCancel();
      }
    } catch (err: any) {
      console.error('Failed to cancel job:', err);
      setError(err.response?.data?.message || 'Failed to cancel job');
      setShowCancelModal(false);
    } finally {
      setIsCanceling(false);
    }
  };

  const handlePrioritizeClick = () => {
    setShowPrioritizeModal(true);
  };

  const calculateTokenCost = (): number => {
    if (!jobStatus?.queuePosition) return 50;
    const TOKENS_PER_POSITION = 50;
    const positionsToSkip = jobStatus.queuePosition;
    return Math.min(Math.max(positionsToSkip * TOKENS_PER_POSITION, 50), 500);
  };

  const handleConfirmPrioritize = async () => {
    try {
      setIsPrioritizing(true);
      const response = await apiClient.post(`/taxonomy/generate-ai/${jobId}/prioritize`);
      const data = response.data.data;

      setShowPrioritizeModal(false);

      // Show success message
      // The job should move to position 1 in the queue
      // Refresh will happen automatically via polling
    } catch (err: any) {
      console.error('Failed to prioritize job:', err);
      setError(err.response?.data?.message || 'Failed to prioritize job');
      setShowPrioritizeModal(false);
    } finally {
      setIsPrioritizing(false);
    }
  };

  if (!jobStatus) return null;

  return (
    <div className="mb-6 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 rounded">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Minimal spinner */}
            {(jobStatus.state === 'waiting' || jobStatus.state === 'active') && (
              <div className="w-4 h-4 border-2 border-gray-900 dark:border-white border-t-transparent dark:border-t-transparent rounded-full animate-spin"></div>
            )}
            <h3 className="text-base font-medium text-gray-900 dark:text-white">
              {jobStatus.state === 'waiting' && t('settings.taxonomy.statusWaiting', 'AI Taxonomy Generation Queued')}
              {jobStatus.state === 'active' && t('settings.taxonomy.statusActive', 'Generating AI Taxonomy...')}
              {jobStatus.state === 'completed' && t('settings.taxonomy.statusCompleted', 'Taxonomy Generated!')}
              {jobStatus.state === 'failed' && t('settings.taxonomy.statusFailed', 'Generation Failed')}
              {jobStatus.state === 'delayed' && t('settings.taxonomy.statusDelayed', 'Generation Delayed')}
            </h3>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
              {getElapsedTime()}
            </span>
            {/* Prioritize button - only show if waiting and position > 1 */}
            {jobStatus.state === 'waiting' && jobStatus.queuePosition && jobStatus.queuePosition > 1 && (
              <button
                type="button"
                onClick={handlePrioritizeClick}
                disabled={isPrioritizing}
                className="px-3 py-1.5 text-sm font-medium text-white dark:text-gray-900 bg-gray-900 dark:bg-white border-2 border-gray-900 dark:border-white hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPrioritizing ? t('settings.taxonomy.prioritizing', 'Prioritizing...') : t('settings.taxonomy.skipTheLine', 'Skip the Line')}
              </button>
            )}
            {/* Cancel button - only show if waiting */}
            {jobStatus.state === 'waiting' && (
              <button
                type="button"
                onClick={handleCancelClick}
                disabled={isCanceling}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-950 border-2 border-gray-200 dark:border-gray-800 hover:border-gray-900 dark:hover:border-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCanceling ? t('common.canceling', 'Canceling...') : t('common.cancel', 'Cancel')}
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {(jobStatus.state === 'waiting' || jobStatus.state === 'active') && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
              <span>{t('settings.taxonomy.progress', 'Progress')}</span>
              <div className="flex items-center gap-2">
                {jobStatus.tokensUsed !== undefined && (
                  <span className="tabular-nums">
                    {jobStatus.tokensUsed.toLocaleString()} tokens
                  </span>
                )}
                <span className="tabular-nums">{jobStatus.progress}%</span>
              </div>
            </div>
            <div className="w-full h-1 bg-gray-100 dark:bg-gray-900 rounded-full overflow-hidden">
              <div
                className="h-full bg-gray-900 dark:bg-white transition-all duration-500 ease-out"
                style={{ width: `${jobStatus.progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {jobStatus.state === 'waiting' && t('settings.taxonomy.waitingDescription', 'Your taxonomy generation is queued. This usually takes 5-10 minutes.')}
          {jobStatus.state === 'active' && (
            jobStatus.message || t('settings.taxonomy.activeDescription', 'GPT-4o is analyzing your website and creating a knowledge structure. This takes 5-10 minutes.')
          )}
          {jobStatus.state === 'completed' && t('settings.taxonomy.completedDescription', 'Your taxonomy has been successfully generated and is now available.')}
          {jobStatus.state === 'failed' && (error || t('settings.taxonomy.failedDescription', 'Something went wrong during generation.'))}
        </p>

        {/* Queue information */}
        {jobStatus.state === 'waiting' && jobStatus.queuePosition && (
          <div className="flex items-center gap-6 text-sm border-t border-gray-100 dark:border-gray-900 pt-3 mt-3">
            <div className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-400">
                {t('settings.taxonomy.queuePosition', 'Queue position')}
              </span>
              <span className="text-gray-900 dark:text-white font-medium tabular-nums">
                #{jobStatus.queuePosition}
              </span>
              <span className="text-gray-400 dark:text-gray-600 text-xs">
                ({t('settings.taxonomy.of', 'of')} {jobStatus.totalInQueue})
              </span>
            </div>
            {jobStatus.estimatedStartTime && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400">
                  {t('settings.taxonomy.estimatedStart', 'Starts in')}
                </span>
                <span className="text-gray-900 dark:text-white font-medium tabular-nums">
                  {getTimeUntilStart()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-950 border-2 border-gray-900 dark:border-white w-full max-w-md">
            <div className="p-6 border-b-2 border-gray-900 dark:border-white">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('settings.taxonomy.cancelJobTitle', 'Cancel Taxonomy Generation?')}
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                {t('settings.taxonomy.cancelJobDescription', 'Are you sure you want to cancel this taxonomy generation job? This action cannot be undone.')}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowCancelModal(false)}
                  disabled={isCanceling}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-950 border-2 border-gray-200 dark:border-gray-800 hover:border-gray-900 dark:hover:border-white transition-colors disabled:opacity-50"
                >
                  {t('common.back', 'Back')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmCancel}
                  disabled={isCanceling}
                  className="px-4 py-2 text-sm font-medium text-white dark:text-gray-900 bg-gray-900 dark:bg-white border-2 border-gray-900 dark:border-white hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCanceling ? t('common.canceling', 'Canceling...') : t('settings.taxonomy.confirmCancel', 'Yes, Cancel Job')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prioritize Confirmation Modal */}
      {showPrioritizeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-950 border-2 border-gray-900 dark:border-white w-full max-w-md">
            <div className="p-6 border-b-2 border-gray-900 dark:border-white">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('settings.taxonomy.prioritizeJobTitle', 'Skip the Line?')}
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('settings.taxonomy.prioritizeJobDescription', 'Move your job to the front of the queue and start processing immediately.')}
              </p>

              {/* Token Cost Display */}
              <div className="bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {t('settings.taxonomy.currentPosition', 'Current position')}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    #{jobStatus?.queuePosition}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {t('settings.taxonomy.positionsToSkip', 'Positions to skip')}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {jobStatus?.queuePosition ? jobStatus.queuePosition - 1 : 0}
                  </span>
                </div>
                <div className="border-t-2 border-gray-200 dark:border-gray-800 my-3"></div>
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold text-gray-900 dark:text-white">
                    {t('settings.taxonomy.tokenCost', 'Token cost')}
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white tabular-nums">
                    {calculateTokenCost()} {t('common.tokens', 'tokens')}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowPrioritizeModal(false)}
                  disabled={isPrioritizing}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-950 border-2 border-gray-200 dark:border-gray-800 hover:border-gray-900 dark:hover:border-white transition-colors disabled:opacity-50"
                >
                  {t('common.back', 'Back')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmPrioritize}
                  disabled={isPrioritizing}
                  className="px-4 py-2 text-sm font-medium text-white dark:text-gray-900 bg-gray-900 dark:bg-white border-2 border-gray-900 dark:border-white hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPrioritizing ? t('settings.taxonomy.prioritizing', 'Prioritizing...') : t('settings.taxonomy.confirmPrioritize', 'Yes, Skip the Line')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
